import { Loader2, Package, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from "@/lib/utils"
import { Badge } from '@/components/ui/badge'
import { SearchProductOutputItem } from '@/lib/api/products'

interface ProductDropdownProps {
    products?: SearchProductOutputItem[]
    isLoading: boolean
    onSelect: (product: SearchProductOutputItem) => void
    showAbove?: boolean
}

const DropdownHeader = ({ title }: { title: string }) => {
    return (
        <div className="sticky top-0 bg-background border-b px-4 py-3 z-10">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
    )
}

const getProductImage = (product: SearchProductOutputItem): string | null => {
    const defaultVariant = product.variants.find((v) => v.default)
    return defaultVariant?.imageUrl ?? product.variants[0]?.imageUrl ?? null
}

export const ProductDropdown = ({ products, isLoading, onSelect, showAbove }: ProductDropdownProps) => {
    const { t } = useTranslation('common')
    return (
        <div className={cn(
            "absolute z-[10001] w-full bg-background border rounded-lg shadow-lg max-h-[300px] overflow-hidden flex flex-col",
            showAbove ? "bottom-full mb-1" : "top-full mt-1"
        )}>
            <DropdownHeader title={t('formComponents.selectProduct')} />
            <div className="overflow-y-auto">
                {isLoading && (
                    <div className="flex items-center justify-center p-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                )}
                {!isLoading &&
                    products?.map((product) => {
                        const imageUrl = getProductImage(product)

                        return (
                            <div
                                key={product.id}
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    onSelect(product)
                                }}
                                className={cn(
                                    'flex items-center gap-3 p-3 cursor-pointer transition-colors',
                                    'hover:bg-accent border-b last:border-b-0',
                                )}
                            >
                                <div className="flex-shrink-0 w-12 h-12 bg-muted rounded-md overflow-hidden flex items-center justify-center">
                                    {imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Package className="w-6 h-6 text-muted-foreground" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium truncate">{product.name}</p>
                                        {!product.active && (
                                            <Badge variant="secondary">{t('formComponents.inactive')}</Badge>
                                        )}
                                    </div>
                                    {product.description && (
                                        <p className="text-sm text-muted-foreground truncate">
                                            {product.description}
                                        </p>
                                    )}
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                        )
                    })}
            </div>
        </div>
    )
}

